// дропзона для загрузки файла

import dropzoneStates from "./data/states";
import dropzoneProgress from "./data/progress";
import border from "./border";
import progressbar from "./progressbar";
import dropzoneFile from "./file";
import errorMessage from "../../domain/error-message";

import popupsState from "../../popups/popups-state";
import Analytics from "../../marketing/analytics";

const template = `
    <div
        class="dropzone"
        :class="[multiple?'dropzone--multi':'', !isAuthenticated && 'dropzone--anonymous']"
        data-ref="dropzone-uploader"
    >
        <border :fill="dropzoneFill"></border>
        <div class="dropzone__upload-wrapper" v-if="isInitialState || multiple || !isAuthenticated">
            <template v-if="!isAuthenticated">
                <div class="dropzone__header" data-test="dropzone-anonimous-header">
                    Чтобы загружать Excel и CSV,<br>
                    <a
                        href="#registration_popup"
                        class="link-white"
                        @click.prevent="handleSignupClick"
                    >зарегистрируйтесь</a>
                    или
                    <a
                        href="#authorization_popup"
                        class="link-white"
                        @click.prevent="handleSigninClick"
                    >
                        войдите в аккаунт
                    </a>
                </div>
            </template>
            <template v-else>
                <input
                    data-ref="dropzone-input"
                    v-show="false"
                    type="file"
                    ref="file"
                    @change="upload"
                    :multiple="multiple"
                />
                <div class="dropzone__upload button-clean button-clean--medium"
                    data-test="dropzone-upload-button"
                    @click.prevent="chooseFile" @drop.prevent="drop"
                    @dragover.prevent @dragenter.prevent="setDragging(true)"
                    @dragleave.prevent="setDragging(false)">
                        [[ text ]]
                </div>
                <div
                    class="dropzone__header"
                    v-html="isMobile && mobileHeader ? mobileHeader : header"
                >
                </div>
                <div v-if="error" class="dropzone__error" v-html="error"></div>
                <div class="dropzone__note">[[ isMobile && mobileHeader ? mobileNote : note ]]</div>
                <template v-if="multiple">
                    <div class="dropzone-files" data-ref="dropzone-files">
                        <transition-group name="slide-in-up">
                            <dropzone-file
                                data-ref="dropzone-file"
                                v-for="(file, index) in filesForUpload"
                                :file="file"
                                :url="url"
                                :index="index"
                                :key="index"
                                :data-document-id="file.id"
                                @uploadingFinished="updateFilesList"
                                @delete:file="deleteFile(index)"
                                >
                            </dropzone-file>
                        </transition-group>
                    </div>
                    <transition name="fade">
                        <div class="forms dropzone__button-block" v-if="uploadedFiles?.length">
                            <button
                                data-ref="dropzone-submit"
                                type="submit"
                                href="#"
                                class="dropzone__button button accented xx-big"
                                :disabled="!isInitialState"
                                v-html="uploadButtonText"
                            >
                            </button>
                        </div>
                    </transition>
                </template>
            </template>
        </div>
        <progressbar
            v-if="!multiple && !isInitialState && isAuthenticated"
            class="dropzone__progress"
            textClass="dropzone__header"
            :progress="progress"
        >
        </progressbar>
    </div>`;

const props = {
    multiple: false,
    uploadedFiles: [],
    state: "",
    error: "",
    header: "",
    note: "",
    mobileHeader: "",
    mobileNote: "",
    filesForUpload: [],
    url: "",
    buttonText: "",
};

const data = function () {
    return {
        isDragging: false,
        progress: null,
        fileIndex: 0,
        isMobile: false,
        isAuthenticated: window.DA.user.isAuthenticated,
    };
};

const methods = {
    upload() {
        this.setDragging(false);
        if (!this.canUpload(this.files())) {
            this.tooManyFiles();
            return;
        }
        this.$emit(
            "dropzone:upload",
            this.files(),
            this.getProgress.bind(this),
        );
        this.clearFileInput();
    },

    canUpload(files = []) {
        if (!this.multiple) {
            return true;
        }
        const total = this.filesWithoutErrors().length;
        if (!files.length) {
            return total < this.maxFiles;
        }
        return total + files.length <= this.maxFiles;
    },

    filesWithoutErrors() {
        return this.filesForUpload.filter(file => file.state !== dropzoneStates.STATES.ERROR);
    },

    chooseFile() {
        if (!this.canUpload()) {
            this.tooManyFiles();
        }
        this.$refs.file.click();
    },

    drop(e) {
        this.$refs.file.files = e.dataTransfer.files;
        this.upload();
    },

    files() {
        if (!this.$refs.file) {
            return [];
        }
        return this.$refs.file.files;
    },

    clearFileInput() {
        this.$refs.file.value = "";
    },

    setDragging(dragging) {
        this.isDragging = dragging;
    },

    getProgress(e) {
        this.progress = dropzoneProgress.getPercentValue(e);
    },

    tooManyFiles() {
        this.setErrors(errorMessage.TOO_MANY_FILES);
    },

    setState(state) {
        this.$emit("update:state", state);
    },

    updateFilesList(errors) {
        this.$emit("update:files", errors);
    },

    setErrors(errors) {
        this.$emit("update:errors", errors);
    },

    deleteFile(index) {
        this.$emit("delete:file", index);
    },

    handleSignupClick() {
        popupsState.dispatchOpen({
            newPopupId: "registration_popup",
            popupData: { signupSource: "DROPZONE" },
        });
    },

    handleSigninClick() {
        popupsState.dispatchOpen({ newPopupId: "authorization_popup" });
        Analytics.reachGoal("SIGNIN_INTERESTED_DROPZONE");
    },
};

const computed = {
    text() {
        return this.multiple ? "Загрузить файлы" : "Загрузить файл";
    },
    isInitialState() {
        return dropzoneStates.isInitial(this.state);
    },
    isInactiveForm() {
        return this.isDragging || !this.isInitialState;
    },
    inactiveClass() {
        return this.isInactiveForm ? "dropzone__border--inactive" : "";
    },
    dropzoneFill() {
        return this.isDragging ? "#275A8D" : "none";
    },

    key() {
        return this.fileIndex++;
    },

    uploadButtonText() {
        const filesCount = this.uploadedFiles.length;
        const plural = filesCount === 1 ? "файле" : "файлах";
        return `${this.buttonText} в&nbsp;${this.uploadedFiles.length}&nbsp;${plural}`;
    },

    maxFiles() {
        return this.$root.$data.maxFiles || 1;
    },
};

const emits = ["delete:file", "update:errors", "update:files", "update:state", "dropzone:upload"];

export default {
    methods,
    template,
    props,
    computed,
    data,
    components: {
        border,
        dropzoneFile,
        progressbar,
    },
    emits,
    mounted() {
        const mql = window.matchMedia("(max-width: 972px)");
        this.isMobile = mql.matches;

        mql.addEventListener("change", e => {
            this.isMobile = e.matches;
        });
    },
};
